import { useEffect, useState, useRef } from "react";
import { Sparkle, PaperPlaneRight, Image as ImageIcon, PenNib, Gauge } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ResearchPanel } from "@/components/Research";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const SUGGESTIONS = [
  "Which bookings are late or unassigned?",
  "What money is outstanding right now?",
  "Which items are low on stock?",
  "What did we earn by lane?",
];

export default function AI() {
  const [view, setView] = useState("copilot");
  const [thread, setThread] = useState([]);
  const [session, setSession] = useState(null);
  const [question, setQuestion] = useState("");
  const [busy, setBusy] = useState(false);
  const [briefing, setBriefing] = useState(null);
  const [kind, setKind] = useState("advert");
  const [brief, setBrief] = useState("");
  const [written, setWritten] = useState("");
  const [caption, setCaption] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/ai/briefing").then((r) => setBriefing(r.data.text)).catch(() => {});
  }, []);

  const ask = async (text) => {
    const q = (text || question).trim();
    if (!q) return;
    setBusy(true);
    setThread((t) => [...t, { role: "you", text: q }]);
    setQuestion("");
    try {
      const { data } = await api.post("/ai/copilot", { question: q, session_id: session });
      setSession(data.session_id);
      setThread((t) => [...t, { role: "ai", text: data.answer }]);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const write = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/ai/content", { kind, brief });
      setWritten(data.text);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const captionPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setCaption("");
    const form = new FormData();
    form.append("file", file);
    form.append("hint", "inventory or delivery photo");
    try {
      const { data } = await api.post("/ai/caption", form, { headers: { "Content-Type": "multipart/form-data" } });
      setCaption(data.caption);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div data-testid="ai-page">
      <PageHeader
        overline="Intelligence"
        title="AI Assistant"
        description="Ask about your live operation, get a morning briefing, have adverts and captions written, and let AI describe your photos."
        testid="ai-header"
      />

      <div className="mb-6 border border-[#1E293B] bg-[#0F172A] p-5" data-testid="briefing-panel">
        <div className="flex items-center gap-2">
          <Sparkle size={16} className="text-[#63A0FF]" />
          <span className="overline text-[0.65rem] text-[#63A0FF]">Today's briefing</span>
        </div>
        <div className="mt-3 whitespace-pre-line text-sm leading-relaxed text-slate-200" data-testid="briefing-text">
          {(briefing || "Building your briefing…").replace(/\*\*/g, "")}
        </div>
      </div>

      <Tabs value={view} onValueChange={setView} className="mb-4 w-full overflow-x-auto">
        <TabsList data-testid="ai-tabs" className="w-max">
          <TabsTrigger value="copilot" data-testid="tab-copilot">Copilot</TabsTrigger>
          <TabsTrigger value="write" data-testid="tab-write">Write for me</TabsTrigger>
          <TabsTrigger value="photo" data-testid="tab-photo">Photo captions</TabsTrigger>
          <TabsTrigger value="research" data-testid="tab-research">Web research</TabsTrigger>
        </TabsList>
      </Tabs>

      {view === "research" && (
        <div data-testid="ai-research">
          <p className="mb-3 max-w-2xl text-sm text-slate-600">
            The copilot answers from your own data. This searches the live web and shows you
            where every answer came from — markets, companies, duty rates and rules.
          </p>
          <ResearchPanel />
        </div>
      )}

      {view === "copilot" && (
        <div className="border border-border bg-white" data-testid="copilot-panel">
          <div className="max-h-[26rem] space-y-3 overflow-y-auto p-4" data-testid="copilot-thread">
            {thread.length === 0 && (
              <div className="flex flex-wrap gap-2">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    data-testid={`suggestion-${s.slice(0, 10)}`}
                    onClick={() => ask(s)}
                    className="border border-border px-3 py-2 text-xs text-slate-600 transition-colors hover:border-[#002FA7] hover:text-[#002FA7]"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
            {thread.map((m, i) => (
              <div key={i} className={m.role === "you" ? "text-right" : ""} data-testid={`msg-${m.role}-${i}`}>
                <div className={`inline-block max-w-[85%] whitespace-pre-line px-3 py-2 text-sm ${
                  m.role === "you" ? "bg-[#002FA7] text-left text-white" : "border border-border bg-slate-50"
                }`}>
                  {m.text.replace(/\*\*/g, "")}
                </div>
              </div>
            ))}
            {busy && <div className="text-xs text-slate-400" data-testid="copilot-thinking">Thinking…</div>}
          </div>
          <form
            onSubmit={(e) => { e.preventDefault(); ask(); }}
            className="flex gap-2 border-t border-border p-3"
          >
            <Input
              data-testid="copilot-input"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="Ask about bookings, money, stock, carriers…"
            />
            <Button type="submit" data-testid="copilot-send" disabled={busy}>
              <PaperPlaneRight size={16} />
            </Button>
          </form>
        </div>
      )}

      {view === "write" && (
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="border border-border bg-white p-4">
            <div className="grid gap-3">
              <div>
                <Label>What should I write?</Label>
                <Select value={kind} onValueChange={setKind}>
                  <SelectTrigger data-testid="content-kind-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="advert">Advert description</SelectItem>
                    <SelectItem value="caption">Story caption + hashtags</SelectItem>
                    <SelectItem value="update">Customer update message</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Brief</Label>
                <Textarea
                  data-testid="content-brief-input"
                  rows={5}
                  value={brief}
                  onChange={(e) => setBrief(e.target.value)}
                  placeholder="e.g. 120 surplus steel brackets, unused, $180 the lot, collection from Detroit"
                />
              </div>
              <Button data-testid="content-generate-button" onClick={write} disabled={busy || !brief.trim()}>
                <PenNib size={16} className="mr-1.5" /> {busy ? "Writing…" : "Write it"}
              </Button>
            </div>
          </div>
          <div className="border border-border bg-white p-4">
            <div className="overline mb-2 text-[0.6rem] text-slate-500">Result</div>
            <div className="whitespace-pre-line text-sm text-slate-700" data-testid="content-result">
              {written || "Your copy will appear here."}
            </div>
            {written && (
              <Button
                size="sm"
                variant="outline"
                className="mt-3"
                data-testid="copy-content-button"
                onClick={() => { navigator.clipboard?.writeText(written)?.catch(() => {}); toast.success("Copied"); }}
              >
                Copy
              </Button>
            )}
          </div>
        </div>
      )}

      {view === "photo" && (
        <div className="border border-border bg-white p-5" data-testid="photo-panel">
          <Label>Upload a photo (JPEG, PNG or WEBP)</Label>
          <input
            ref={fileRef}
            data-testid="ai-photo-input"
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={captionPhoto}
            className="mt-2 block w-full border border-border p-2 text-base md:text-sm"
          />
          <div className="mt-4 flex items-start gap-2 text-sm text-slate-700" data-testid="ai-caption-result">
            <ImageIcon size={16} className="mt-0.5 text-[#002FA7]" />
            <span>{busy ? "Looking at your photo…" : caption || "The AI description will appear here."}</span>
          </div>
        </div>
      )}

      <p className="mt-6 flex items-center gap-1.5 text-xs text-slate-400">
        <Gauge size={13} /> Powered by GPT-5.5 on your Emergent Universal Key.
      </p>
    </div>
  );
}
